import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getCookieChoice } from '../utils/analytics';
import { getWelcomeOffer, offerHeadline } from '../utils/welcomeOffer';
import '../assets/styles/offer-popup.css';

const SEEN_KEY = 'welcomeOfferSeenAt';
const SHOW_AGAIN_AFTER = 30 * 24 * 60 * 60 * 1000;
const SHOW_AFTER = 15 * 1000;
const CHECK_EVERY = 3 * 1000;

// Never interrupt someone buying, signing in or reading the small print
const QUIET_PAGES = /^\/(cart|checkout|shipping|payment|placeorder|order|login|register|reset|verify-email|admin|profile|orders|unsubscribe|cookie|privacy|terms)/i;

const seenRecently = () => {
    try {
        const seenAt = Number(localStorage.getItem(SEEN_KEY));
        return Boolean(seenAt) && Date.now() - seenAt < SHOW_AGAIN_AFTER;
    } catch {
        return false;
    }
};

const markSeen = () => {
    try {
        localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch {
        // Storage blocked: the popup may come back on the next visit
    }
};

const WelcomeOfferPopup = () => {
    const { pathname } = useLocation();
    const { userInfo } = useSelector((state) => state.auth);
    const [offer, setOffer] = useState(null);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');
    const pathRef = useRef(pathname);
    pathRef.current = pathname;

    const isQuietPage = QUIET_PAGES.test(pathname);

    useEffect(() => {
        if (userInfo || seenRecently()) return undefined;
        const startedAt = Date.now();
        let finished = false;

        const timer = setInterval(async () => {
            // Wait for the cookie question to be answered so the two never stack up
            if (Date.now() - startedAt < SHOW_AFTER || !getCookieChoice() || QUIET_PAGES.test(pathRef.current)) return;
            clearInterval(timer);
            const result = await getWelcomeOffer();
            if (!finished && result) setOffer(result);
        }, CHECK_EVERY);

        return () => {
            finished = true;
            clearInterval(timer);
        };
    }, [userInfo]);

    const close = () => {
        markSeen();
        setOffer(null);
    };

    useEffect(() => {
        if (!offer) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [offer]);

    const submit = async (event) => {
        event.preventDefault();
        setStatus('sending');
        try {
            const { data } = await axios.post('/api/newsletter/subscribe', { email, source: 'popup' });
            markSeen();
            setMessage(data.message);
            setStatus('done');
        } catch (error) {
            setMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
            setStatus('error');
        }
    };

    if (!offer || isQuietPage) return null;

    return (
        <aside className="rd-offer" role="dialog" aria-labelledby="rd-offer-title">
            <button type="button" className="rd-offer__close" onClick={close} aria-label="Close offer">
                <FaTimes aria-hidden="true" />
            </button>
            {status === 'done' ? (
                <p id="rd-offer-title" className="rd-offer__done" role="status">{message}</p>
            ) : (
                <>
                    <h2 id="rd-offer-title">{offerHeadline(offer)}</h2>
                    <p>Join our email list for new arrivals and deals, and we'll send your code.</p>
                    <form onSubmit={submit} className="rd-offer__form">
                        <label htmlFor="rd-offer-email" className="visually-hidden">Email address</label>
                        <input
                            id="rd-offer-email"
                            type="email"
                            placeholder="Enter your email"
                            autoComplete="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                            disabled={status === 'sending'}
                        />
                        <button type="submit" disabled={status === 'sending'}>
                            {status === 'sending' ? 'Sending...' : 'Send My Code'}
                        </button>
                    </form>
                    {status === 'error' && <p className="rd-offer__error" role="alert">{message}</p>}
                    <small>Unsubscribe anytime. See our <Link to="/privacy">Privacy Policy</Link>.</small>
                </>
            )}
        </aside>
    );
};

export default WelcomeOfferPopup;
